/**
 * Time Utility Functions
 * Handles time formatting in Central Time (CT)
 */

/**
 * Formats a timestamp to Central Time in 12-hour format
 * @param {number} timestamp - Milliseconds since epoch
 * @returns {string} Formatted time string (e.g., "11:30 PM CT")
 */
export const formatToCentralTime = (timestamp) => {
  const date = new Date(timestamp);

  return date.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
};

/**
 * Formats a timestamp to Central Time with date
 * @param {number} timestamp - Milliseconds since epoch
 * @returns {string} Formatted date and time (e.g., "Jan 15, 2025, 11:30 PM CT")
 */
export const formatDateTimeCT = (timestamp) => {
  const date = new Date(timestamp);

  return date.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
};

/**
 * Formats a timestamp to just the date in Central Time
 * @param {number} timestamp - Milliseconds since epoch
 * @returns {string} Formatted date (e.g., "January 15, 2025")
 */
export const formatDateCT = (timestamp) => {
  const date = new Date(timestamp);

  return date.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Gets current timestamp in milliseconds
 * @returns {number} Current timestamp
 */
export const getCurrentTimestamp = () => {
  return Date.now();
};

/**
 * Formats timestamp to ISO 8601 string
 * @param {number} timestamp - Milliseconds since epoch
 * @returns {string} ISO 8601 formatted string
 */
export const toISOString = (timestamp) => {
  return new Date(timestamp).toISOString();
};

/**
 * Get relative time (e.g., "2 hours ago", "just now")
 * @param {number} timestamp - Milliseconds since epoch
 * @returns {string} Relative time string
 */
export const getRelativeTime = (timestamp) => {
  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;

  return formatDateCT(timestamp);
};
